import { toElementFieldPath } from './automationVariables';

export const AI_OUTPUT_TYPES = ['auto', 'string', 'number', 'boolean'];
const TEXT_TYPES = ['string', 'text', 'textarea', 'email', 'phone', 'url', 'html'];
const SELECTION_TYPES = ['selection', 'status', 'dropdown', 'multi-select'];
const RESERVED_NAMES = ['__proto__', 'constructor', 'prototype'];
const PROTECTED_FIELDS = ['ownerId', 'ownerDetails', 'structureId', 'structureDetails', 'documentPath', 'documentIdentifiant', 'mirror', 'createdBy', 'updatedBy'];

export function isValidAIOutputVariable(name) {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) && !RESERVED_NAMES.includes(name);
}

/** UI guidance only. The backend re-reads and validates the actual Structure. */
export function getAIFieldOutputType(field) {
  if (!field || field.readOnly || field.private || field.formula || field.isFormula ||
    field.isActive === false || field.reference?.native) return null;
  const type = field.typeData;
  const writeKey = (field.value || '').replace(/^data\./, '');
  const rootKey = writeKey.split('.')[0];
  if (rootKey.startsWith('_') || PROTECTED_FIELDS.includes(rootKey)) return null;
  if (writeKey === 'status' && !['number', 'selection', 'status', 'dropdown'].includes(type)) return null;
  if (writeKey === 'groups' && type !== 'multi-select') return null;
  if (TEXT_TYPES.includes(type)) return 'string';
  if (type === 'number' || type === 'boolean') return type;
  if (SELECTION_TYPES.includes(type)) {
    if (!Array.isArray(field.selections) || !field.selections.length) return null;
    if (field.selections.some(selection => selection?.conditional &&
      (!Array.isArray(selection.conditional) || selection.conditional.length))) return null;
    const values = field.selections.map(selection => typeof selection === 'object' && selection !== null
      ? selection.value : selection);
    if (values.some(value => !['string', 'number', 'boolean'].includes(typeof value) ||
      (typeof value === 'number' && !Number.isFinite(value)))) return null;
    if (writeKey === 'status' && values.some(value => !Number.isSafeInteger(value))) return null;
    if (writeKey === 'groups' && values.some(value => typeof value !== 'string')) return null;
    return type === 'multi-select' ? 'array' : new Set(values.map(value => typeof value)).size === 1 ? typeof values[0] : 'selection';
  }
  return null;
}

export function findAIOutputField(fields, path) {
  return fields?.find(field => toElementFieldPath(field) === path || field.value === path);
}

/** Only suggest current-record destinations whose variable still comes from this step. */
export function getAIConnectedFields(step, steps, fields) {
  const variables = new Set(Object.entries(step.outputVariables || {})
    .filter(([, path]) => path === 'value').map(([name]) => name));
  const index = steps.findIndex(item => item.id === step.id);
  if (index < 0) return [];
  const destinations = [];
  for (const next of steps.slice(index + 1)) {
    if (next.type === 'updateField' && !next.config?.targetElement &&
      typeof next.config?.value === 'string' && variables.has(next.config.value.slice(1)) &&
      next.config.value.startsWith('$')) {
      const field = findAIOutputField(fields, next.config.field);
      if (field && !destinations.includes(field)) destinations.push(field);
    }
    if (next.type === 'askAI' || !next.conditions?.length) {
      Object.keys(next.outputVariables || {}).forEach(name => variables.delete(name));
    }
    if (!variables.size) break;
  }
  return destinations;
}

export function getAIOutputCatalogType(step, steps, fields) {
  const explicit = step.config?.outputType ?? 'auto';
  if (explicit !== 'auto') return AI_OUTPUT_TYPES.includes(explicit) ? explicit : undefined;
  const field = step.config?.outputField
    ? findAIOutputField(fields, step.config.outputField)
    : getAIConnectedFields(step, steps, fields)[0];
  return field ? getAIFieldOutputType(field) || undefined : undefined;
}
